import { store } from '@/store';
import { Label, ReportLabels } from '@/features/reports/models';
import Chart from 'chart.js';
import { ChartDataSets } from 'chart.js';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';

export const inlineLabelsPlugin: Chart.PluginServiceGlobalRegistration & Chart.PluginServiceRegistrationOptions = {
	afterInit,
	afterUpdate
};

function afterInit(chart: Chart) {
	const options = chart.options;
	const inlineLabelsOptions = options.plugins!.inlineLabels;

	if (!inlineLabelsOptions) {
		return;
	}

	const reportId = options.plugins!.inlineLabels.reportId;
	const reverseLabels = !!options.plugins!.inlineLabels.reverseLabels;
	const dataFormat = options.plugins!.inlineLabels.dataFormat;

	let defaultLabels: Map<string, string>;
	if (options.plugins!.inlineLabels.defaultLabelValues) {
		defaultLabels = options.plugins!.inlineLabels.defaultLabelValues;
	} else {
		defaultLabels = calculateDefaults(chart, dataFormat);
	}

	setDefaults(reportId, chart, defaultLabels, reverseLabels);

	// register event listener to reset the labels
	chart.canvas!.addEventListener('mouseleave', () => {
		setDefaults(reportId, chart, defaultLabels, reverseLabels);
	});

	if (!options.tooltips) {
		options.tooltips = {};
		options.tooltips.callbacks = {};
	}

	options.tooltips.custom = (tooltipModel: Chart.ChartTooltipModel) => {
		tooltipModel.opacity = 0;
	};

	options.tooltips.callbacks!.afterFooter = (items: Chart.ChartTooltipItem[]) => {
		const xAxisIndex = items[0].index!;
		const xAxisLabel = items[0].label!;
		setLabelsForDataPoint(reportId, xAxisLabel, chart, xAxisIndex, reverseLabels);
		return '';
	};
}

function afterUpdate(chart: Chart) {
	const options = chart.options;

	const reportId = options.plugins!.inlineLabels.reportId;
	const reverseLabels = !!options.plugins!.inlineLabels.reverseLabels;

	const xAxisIndex = chart.data.labels!.length - 1;
	const xAxisLabel = String(chart.data.labels![xAxisIndex]);
	setLabelsForDataPoint(reportId, xAxisLabel, chart, xAxisIndex, reverseLabels);
}

function calculateDefaults(chart: Chart, dataFormat: ChartDataFormat): Map<string, string> {
	const defaultLabels = new Map<string, string>();

	chart.data.datasets!.forEach(dataset => {
		let total = 0;

		dataset.data?.forEach((value: any) => {
			if (typeof value === 'number') {
				total += value;
			}
		});

		defaultLabels.set(dataset.label!, total.toLocaleString());
	});

	if (dataFormat === ChartDataFormat.PERCENTAGE) {
		let overallTotal = 0;
		defaultLabels.forEach((labelValue) => overallTotal += parseInt(labelValue, 10));
		defaultLabels.forEach((labelValue, labelName, totals) => {
			const value = labelValue ? parseInt(labelValue, 10) : 0;
			const percentageOfTotal = overallTotal === 0 ? 0 : value / overallTotal * 100;
			const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;
			totals.set(labelName, `${roundedPercentage.toString()}%`);
		});
		return defaultLabels;
	}
	return defaultLabels;
}

function setDefaults(reportId: string, chart: Chart, labelData: Map<string, string>, reverseLabels: boolean) {
	const visibleChartData = getVisibleChartData(chart);

	const labels = visibleChartData.map((dataset: ChartDataSets) => ({
		label: dataset.label,
		value: labelData.get(dataset.label!),
		borderColour: dataset.borderColor,
		fillColour: dataset.backgroundColor,
	}));

	store.commit('SET_LABELS', {
		reportId,
		scaleTitle: 'Period',
		scaleValue: store.getters.selectedDateOption.label,
		labels,
		reverse: reverseLabels
	} as ReportLabels);
}

function setLabelsForDataPoint(reportId: string, scaleValue: string, chart: Chart, xAxisIndex: number, reverseLabels: boolean) {
	const visibleData = getVisibleChartData(chart);

	store.commit('SET_LABELS', {
		reportId,
		scaleTitle: getTitle(chart),
		scaleValue,
		labels: visibleData.map((dataset, dataSetIndex) => {
			return {
				label: dataset.label,
				value: getDisplayValue(chart, dataset.data![xAxisIndex], xAxisIndex, dataSetIndex),
				borderColour: dataset.borderColor,
				fillColour: dataset.backgroundColor,
			} as Label;
		}),
		reverse: reverseLabels
	} as ReportLabels);
}

function getVisibleChartData(chart: Chart): ChartDataSets[] {
	const dataSet = chart.data.datasets!;

	if (!(chart as any).legend.legendItems) {
		return dataSet;
	}
	const enabledLegendItems = (chart as any).legend.legendItems.filter((item: any) => !item.hidden) as any[];
	return dataSet.filter(data => enabledLegendItems.find(item => item.text === data.label));
}

function getTitle(chart: Chart) {
	if (chart.options.scales) {
		return chart.options.scales!.xAxes![0].scaleLabel!.labelString!;
	} else {
		return '-';
	}
}

function getDisplayValue(chart: Chart, value: any, index: number, datasetIndex: number): string {
	if (chart.options.tooltips && chart.options.tooltips!.callbacks && chart.options.tooltips!.callbacks.label) {
		const formattedValue = chart.options.tooltips!.callbacks.label({ value, index, datasetIndex }, chart.data);
		return String(formattedValue);
	}

	return value ? String(value) : '';
}
